import React from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import { stopDisplayState } from '../../../utils/composeDriverRecords';
import popupCardBase from './popupCardStyles';
import {
  formatDeltaLabel,
  formatDriverNames,
  formatMiles,
  locationNoun,
} from '../../../utils/displayFormat';
import {
  STOP_STATE_LABEL,
  STOP_STATE_TONE,
  OUT_OF_SEQUENCE_TONE,
  toneClassVariants,
} from './statusStyles';

// Shared stop-detail card used by BOTH the map marker popup (MapPane) and the
// list-view progress-bar popover (RouteListView) so the two always show the
// same fields. Card styles are shared with DriverPopupContent via
// popupCardBase; self-contained (spc-*) so it renders correctly wherever
// it's mounted.

const StyledCard = styledComponent('div', {
  ...popupCardBase('spc'),
  '& .spc-seqrow': {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    marginBottom: '6px',
    fontSize: theme.sizes.captionFontSize,
    color: theme.colors.textMuted,
  },
  '& .spc-tag': {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: theme.radii.pill,
    fontSize: theme.sizes.microFontSize,
    fontWeight: theme.fontWeights.semibold,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  ...toneClassVariants('spc-tag', STOP_STATE_TONE),
  '& .spc-tag.out-of-seq': {
    background: OUT_OF_SEQUENCE_TONE.bg,
    color: OUT_OF_SEQUENCE_TONE.fg,
  },
  '& .spc-type': {
    padding: '6px 0',
    borderTop: `1px solid ${theme.colors.borderLight}`,
    borderBottom: `1px solid ${theme.colors.borderLight}`,
    fontSize: theme.sizes.captionFontSize,
    color: theme.colors.textBody,
    marginBottom: '8px',
  },
  '& .spc-footer': {
    marginTop: '8px',
    paddingTop: '8px',
    borderTop: `1px solid ${theme.colors.borderLight}`,
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: theme.sizes.captionFontSize,
    color: theme.colors.textMuted,
  },
});

function StopPopupContent({ stop, driver }) {
  const displayState = stopDisplayState(stop);
  const stateTone = STOP_STATE_TONE[displayState] ? displayState : 'pending';

  return (
    <StyledCard>
      <div className="spc-name">{stop.stopId}</div>
      <div className="spc-seqrow">
        <span>
          {driver.stopsTotal
            ? `Stop ${stop.stopIndex} of ${driver.stopsTotal}`
            : `Stop #${stop.stopIndex}`}
        </span>
        <span className={`spc-tag ${stateTone}`}>
          {STOP_STATE_LABEL[displayState]}
        </span>
        {stop.outOfSequence && (
          <span className="spc-tag out-of-seq">Out of sequence</span>
        )}
      </div>
      <div className="spc-type">{stop.type}</div>
      <div className="spc-row">
        <span className="spc-label">Planned window</span>
        <span className="spc-value">{stop.plannedWindow}</span>
      </div>
      {/* Traffic-aware leg for the drive TO this stop. */}
      {stop.driveDurationMin !== null && stop.driveDurationMin !== undefined && (
        <>
          <div className="spc-row">
            <span className="spc-label">Drive time</span>
            <span className="spc-value">
              {`${stop.driveDurationMin} min`}
              {stop.staticDurationMin !== null &&
              stop.staticDurationMin !== undefined
                ? ` (${stop.staticDurationMin} min no traffic)`
                : ''}
            </span>
          </div>
          <div className="spc-row">
            <span className="spc-label">Traffic delay</span>
            <span className="spc-value">
              {formatDeltaLabel(stop.trafficDelayMin, {
                unit: 'min',
                zeroLabel: 'none',
              })}
            </span>
          </div>
          {stop.distanceMiles !== null && stop.distanceMiles !== undefined && (
            <div className="spc-row">
              <span className="spc-label">Distance</span>
              <span className="spc-value">
                {formatMiles(stop.distanceMiles)}
              </span>
            </div>
          )}
        </>
      )}
      {stop.state === 'done' && (
        <>
          {stop.scannedAt && (
            <div className="spc-row">
              <span className="spc-label">Scanned</span>
              <span className="spc-value">{stop.scannedAt}</span>
            </div>
          )}
          <div className="spc-row">
            <span className="spc-label">Stocked</span>
            <span className="spc-value">{stop.stockedAt}</span>
          </div>
          {stop.stockedByName && (
            <div className="spc-row">
              <span className="spc-label">Stocked by</span>
              <span className="spc-value">{stop.stockedByName}</span>
            </div>
          )}
          {stop.actualDurationMin !== null &&
          stop.actualDurationMin !== undefined ? (
            <div className="spc-row">
              <span className="spc-label">
                {`Time at ${locationNoun(stop.type)}`}
              </span>
              <span className="spc-value">
                {`${stop.actualDurationMin} min`}
                {Number.isFinite(stop.plannedMin) && stop.plannedMin > 0
                  ? ` (planned ${stop.plannedMin})`
                  : ''}
              </span>
            </div>
          ) : (
            <div className="spc-row">
              <span className="spc-label">Planned window length</span>
              <span className="spc-value">{`${stop.plannedMin} min`}</span>
            </div>
          )}
          <div className="spc-row">
            <span className="spc-label">Finished vs plan</span>
            <span className="spc-value">
              {formatDeltaLabel(stop.delta, { unit: 'min' })}
            </span>
          </div>
        </>
      )}
      {stop.state === 'current' && (
        <>
          {/* The arrival scan is what makes this stop "In progress": show the
              moment the driver scanned in. */}
          {stop.scannedAt && (
            <div className="spc-row">
              <span className="spc-label">Scanned in</span>
              <span className="spc-value">{stop.scannedAt}</span>
            </div>
          )}
          {stop.legEtaLabel && (
            <div className="spc-row">
              <span className="spc-label">ETA (live traffic)</span>
              <span className="spc-value">{stop.legEtaLabel}</span>
            </div>
          )}
          <div className="spc-row">
            <span className="spc-label">Planned window length</span>
            <span className="spc-value">{`${stop.plannedMin} min`}</span>
          </div>
        </>
      )}
      {stop.state === 'pending' && (
        <>
          <div className="spc-row">
            <span className="spc-label">ETA</span>
            <span className="spc-value">{stop.etaLabel}</span>
          </div>
          <div className="spc-row">
            <span className="spc-label">ETA vs planned</span>
            <span className="spc-value">
              {formatDeltaLabel(stop.delta, { unit: 'min' })}
            </span>
          </div>
        </>
      )}
      {/* The route's drivers — everyone seen working it — not this stop's
          stocker, which the "Stocked by" row above already names. */}
      <div className="spc-footer">
        <span>{formatDriverNames(driver.driverNames)}</span>
        <span>{driver.routeName}</span>
      </div>
    </StyledCard>
  );
}

StopPopupContent.propTypes = {
  stop: PropTypes.shape({
    stopIndex: PropTypes.number,
    stopId: PropTypes.string,
    stopName: PropTypes.string,
    type: PropTypes.string,
    state: PropTypes.string,
    outOfSequence: PropTypes.bool,
    skipped: PropTypes.bool,
    plannedWindow: PropTypes.string,
    plannedMin: PropTypes.number,
    scannedAt: PropTypes.string,
    stockedAt: PropTypes.string,
    actualDurationMin: PropTypes.number,
    etaLabel: PropTypes.string,
    delta: PropTypes.number,
    driveDurationMin: PropTypes.number,
    staticDurationMin: PropTypes.number,
    trafficDelayMin: PropTypes.number,
    distanceMiles: PropTypes.number,
    legEtaLabel: PropTypes.string,
    stockedByName: PropTypes.string,
  }).isRequired,
  driver: PropTypes.shape({
    driverNames: PropTypes.arrayOf(PropTypes.string),
    routeName: PropTypes.string,
    stopsTotal: PropTypes.number,
  }).isRequired,
};

export default StopPopupContent;
